"use client";

/**
 * PDF Viewer Component
 *
 * Uses pdfjs-dist to render PDF documents with citation text highlighting.
 *
 * ## PDF.js Worker Configuration
 *
 * The PDF.js library requires a web worker for parsing PDFs. The worker file
 * is kept in sync with the pdfjs-dist version automatically:
 *
 * 1. `postinstall` script copies the worker after `npm install`
 * 2. `prebuild` script copies the worker before each build
 *
 * This ensures the worker version always matches the installed pdfjs-dist.
 *
 * ### When updating pdfjs-dist:
 * - Run `npm install` (postinstall handles the copy)
 * - Or run `npm run build` (prebuild handles the copy)
 * - The worker at `/public/pdf.worker.min.mjs` will be updated automatically
 *
 * @see package.json for the postinstall and prebuild scripts
 */

import React from "react";
// PINNED to 5.4.624 — do not upgrade past 5.4.x until Map.getOrInsertComputed
// has broad browser support. pdfjs-dist 5.5.207+ uses this TC39 Stage 4 API
// which is missing in Safari, Firefox, and some Chromium builds. Upgrading
// causes silent render failures (blank canvas, no error in UI).
// Revisit after Safari 19+ and Firefox 140+ ship with support (~Q3 2026).
import * as pdfjsLib from "pdfjs-dist";
import { Spinner } from "@heroui/react";
import { PDFDocumentProxy, TextItem } from "pdfjs-dist/types/src/display/api";

import { findTextMatch, normalizeText, MatchResult } from "@/utils/textMatching";
import { logger } from "@/telemetry";

// Configure PDF.js worker for version 5.x
// The worker file is copied to public/ folder via postinstall/prebuild scripts
// This avoids external CDN dependency and ensures version compatibility
if (typeof window !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
}

export interface PDFViewerProps {
  /** PDF document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Text to find and highlight in the PDF */
  citationText?: string;

  /** Callback when text is found or not found */
  onMatchResult?: (result: MatchResult | null) => void;
}

interface TextLayerItem {
  text: string;
  transform: number[];
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * PDF Viewer component using PDF.js
 * Renders PDF pages and highlights citation text
 */
export const PDFViewer: React.FC<PDFViewerProps> = ({
  documentData,
  citationText,
  onMatchResult,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [pdfDocument, setPdfDocument] = React.useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = React.useState(0);
  const [textLayer, setTextLayer] = React.useState<TextLayerItem[]>([]);
  const [matchedPage, setMatchedPage] = React.useState<number | null>(null);
  const canvasRefs = React.useRef<Map<number, HTMLCanvasElement>>(new Map());
  const highlightRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());
  const [pageHighlights, setPageHighlights] = React.useState<
    Record<number, Array<{ left: number; top: number; width: number; height: number }>>
  >({});

  // Load PDF document
  React.useEffect(() => {
    let isMounted = true;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        const loadingTask = pdfjsLib.getDocument({ data: documentData });
        const pdf = await loadingTask.promise;

        if (!isMounted) return;

        setPdfDocument(pdf);
        setNumPages(pdf.numPages);
        logger.info("📄 PDF loaded", { numPages: pdf.numPages });
      } catch (err) {
        logger.error("Failed to load PDF", err);
        if (isMounted) {
          setError("Failed to load PDF document");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      isMounted = false;
    };
  }, [documentData]);

  // Extract text layer from all pages
  React.useEffect(() => {
    if (!pdfDocument) return;

    let isMounted = true;

    const extractTextLayer = async () => {
      try {
        const items: TextLayerItem[] = [];

        // IMPORTANT: Use the same scale as canvas rendering (1.5)
        const scale = 1.5;

        for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
          const page = await pdfDocument.getPage(pageNum);
          const textContent = await page.getTextContent();
          const viewport = page.getViewport({ scale });

          textContent.items.forEach((item) => {
            if ("str" in item) {
              const textItem = item as TextItem;
              const transform = textItem.transform;

              // Calculate position from transform matrix
              // transform is [scaleX, skewY, skewX, scaleY, translateX, translateY]
              // These coordinates are at the same scale as the canvas
              const x = transform[4] * scale;
              const y = viewport.height - transform[5] * scale; // Flip Y coordinate
              const width = textItem.width * scale;
              const height = textItem.height * scale;

              items.push({
                text: textItem.str,
                transform,
                page: pageNum,
                x,
                y,
                width,
                height,
              });
            }
          });
        }

        if (isMounted) {
          setTextLayer(items);
          logger.info("📝 Extracted text from PDF pages", {
            numPages: pdfDocument.numPages,
            itemCount: items.length,
          });
        }
      } catch (err) {
        logger.error("Failed to extract text layer", err);
      }
    };

    extractTextLayer();

    return () => {
      isMounted = false;
    };
  }, [pdfDocument]);

  // Find citation text in extracted text layer
  React.useEffect(() => {
    if (!citationText || textLayer.length === 0) return;

    const documentText = textLayer.map((item) => item.text).join(" ");

    logger.info("🔍 Looking for citation", {
      citationPreview: citationText.substring(0, 100),
      normalizedPreview: normalizeText(citationText).substring(0, 100),
    });

    const matchResult = findTextMatch(documentText, citationText);

    logger.info("🔍 Citation match result", { found: String(!!matchResult) });
    if (matchResult) {
      logger.info("🔍 Found text", {
        matchedTextPreview: matchResult.matchedText.substring(0, 100),
        method: matchResult.method,
        confidence: Math.round(matchResult.confidence * 100) + "%",
      });
    }

    if (onMatchResult) {
      onMatchResult(matchResult);
    }

    // Find start and end across pages and compute cross-page highlights
    if (matchResult) {
      logger.info("🔍 Starting start/end page detection using head/tail segments");

      // Build ordered page list and page texts
      const pageNumbers = Array.from(new Set(textLayer.map((item) => item.page))).sort(
        (a, b) => a - b,
      );

      const getPageTextItems = (p: number) => textLayer.filter((i) => i.page === p);
      const getPageText = (p: number) =>
        getPageTextItems(p)
          .map((i) => i.text)
          .join(" ");

      // Head and tail segments from the citation
      const headLen = Math.min(100, citationText.length);
      const headSeg = citationText.substring(0, headLen);
      const tailLen = Math.min(100, citationText.length);
      const tailSeg = citationText.substring(Math.max(0, citationText.length - tailLen));

      logger.info("🔍 Head/tail segments", {
        headPreview: headSeg.substring(0, 80),
        tailPreview: tailSeg.substring(0, 80),
      });

      // Find best page for head
      let startPage: number | null = null;
      let startConf = 0;

      for (const p of pageNumbers) {
        const pageText = getPageText(p);
        const r = findTextMatch(pageText, headSeg);

        if (r && r.confidence > startConf) {
          startConf = r.confidence;
          startPage = p;
        }
      }
      if (startPage) {
        logger.info("📍 Start page found", {
          startPage,
          confidence: Math.round(startConf * 100) + "%",
        });
      } else {
        logger.info(
          "⚠️ Could not find start page via head segment; falling back to earlier method",
        );
      }

      // Find best page for tail
      let endPage: number | null = null;
      let endConf = 0;

      for (const p of pageNumbers) {
        const pageText = getPageText(p);
        const r = findTextMatch(pageText, tailSeg);

        if (r && r.confidence > endConf) {
          endConf = r.confidence;
          endPage = p;
        }
      }
      if (endPage) {
        logger.info("📍 End page found", { endPage, confidence: Math.round(endConf * 100) + "%" });
      } else {
        logger.info("⚠️ Could not find end page via tail segment (fallback will handle)");
      }

      // Fallback: if no start page determined, try previous page-detection approach (closest match to full citation)
      if (!startPage) {
        let fallbackPage: number | null = null;
        const matchedTextStart = matchResult.matchedText.substring(0, 80);

        for (const p of pageNumbers) {
          const pageText = getPageText(p);

          if (pageText.includes(matchedTextStart)) {
            fallbackPage = p;
            break;
          }
        }
        if (!fallbackPage) {
          // try normalized search
          for (const p of pageNumbers) {
            const pageText = getPageText(p);
            const r = findTextMatch(pageText, citationText);

            if (r && r.confidence >= 0.7) {
              fallbackPage = p;
              break;
            }
          }
        }
        startPage = fallbackPage;
        if (startPage) logger.info("📍 Fallback start page", { startPage });
      }

      if (startPage) {
        // Helper to build normalized page text and per-item start/length offsets
        const buildNormPage = (items: TextLayerItem[]) => {
          const starts: number[] = [];
          const lens: number[] = [];
          const charToItemIndex: number[] = [];
          let text = "";

          const isWordChar = (ch: string) => /[\p{L}\p{N}]/u.test(ch);

          for (let idx = 0; idx < items.length; idx++) {
            const it = items[idx];
            const curr = normalizeText(it.text);
            const prev = idx > 0 ? normalizeText(items[idx - 1].text) : "";

            // Decide whether to insert a space between items.
            // Insert space only if the previous item ends with a word char, the current starts with a word char,
            // and the previous does NOT end with a hyphen (common hyphenation case).
            const prevLast = prev.slice(-1);
            const currFirst = curr.slice(0, 1);
            const shouldInsertSpace =
              idx > 0 && isWordChar(prevLast) && isWordChar(currFirst) && prevLast !== "-";

            if (shouldInsertSpace) {
              text += " ";
              charToItemIndex.push(-1); // separator, belongs to no item
            }

            const start = text.length;

            starts.push(start);

            // Append current normalized text and map each character to this item index
            for (let k = 0; k < curr.length; k++) {
              text += curr[k];
              charToItemIndex.push(idx);
            }

            lens.push(curr.length);
          }

          return { text, starts, lens, charToItemIndex };
        };

        // Determine item index on start page using normalized head match position
        const startItems = getPageTextItems(startPage);
        const normStartPage = buildNormPage(startItems);
        const headMatchOnStart = findTextMatch(normStartPage.text, headSeg);
        let startIndex = 0;

        if (headMatchOnStart) {
          let pos = headMatchOnStart.position;

          // Clamp to valid range
          pos = Math.max(0, Math.min(pos, normStartPage.text.length - 1));
          // Prefer the exact char mapping; if we land on a separator (-1), scan to nearest item char
          const nearestForward = () => {
            for (let p = pos; p < normStartPage.charToItemIndex.length; p++) {
              const idx = normStartPage.charToItemIndex[p];

              if (idx >= 0) return idx;
            }

            return -1;
          };
          const nearestBackward = () => {
            for (let p = pos; p >= 0; p--) {
              const idx = normStartPage.charToItemIndex[p];

              if (idx >= 0) return idx;
            }

            return -1;
          };
          let idx = normStartPage.charToItemIndex[pos];

          if (idx < 0) {
            const f = nearestForward();

            idx = f >= 0 ? f : nearestBackward();
          }
          if (idx >= 0) {
            startIndex = idx;
            logger.info("🎯 Start position mapped via char map", {
              position: pos,
              itemIndex: idx,
              page: startPage,
            });
          }
        } else {
          // Fallback to first-token heuristic
          const headNorm = normalizeText(headSeg);
          const headTokens = headNorm.split(/\s+/).filter(Boolean);
          const firstToken = headTokens[0] || "";

          for (let i = 0; i < startItems.length; i++) {
            if (startItems[i].text.toLowerCase().includes(firstToken)) {
              startIndex = i;
              logger.info("🎯 Start token found via fallback", {
                firstToken,
                itemIndex: i,
                page: startPage,
              });
              break;
            }
          }
        }

        let resolvedEndPage = endPage || startPage;
        const endItems = getPageTextItems(resolvedEndPage);
        const normEndPage = buildNormPage(endItems);
        const tailMatchOnEnd = findTextMatch(normEndPage.text, tailSeg);
        let endIndex = endItems.length - 1;

        if (tailMatchOnEnd) {
          let endPos = tailMatchOnEnd.position + tailMatchOnEnd.length - 1;

          endPos = Math.max(0, Math.min(endPos, normEndPage.text.length - 1));

          const nearestBackward = () => {
            for (let p = endPos; p >= 0; p--) {
              const idx = normEndPage.charToItemIndex[p];

              if (idx >= 0) return idx;
            }

            return -1;
          };
          const nearestForward = () => {
            for (let p = endPos; p < normEndPage.charToItemIndex.length; p++) {
              const idx = normEndPage.charToItemIndex[p];

              if (idx >= 0) return idx;
            }

            return -1;
          };

          // Prefer the item that actually contains the last matched character (backward bias)
          let idx = normEndPage.charToItemIndex[endPos];

          if (idx < 0) {
            // If we landed on a separator, choose the closest item char; prefer backward to avoid overshooting
            const b = nearestBackward();

            idx = b >= 0 ? b : nearestForward();
          }

          if (idx >= 0) {
            endIndex = idx;
            logger.info("🎯 End position mapped via char map", {
              position: endPos,
              itemIndex: idx,
              page: resolvedEndPage,
            });
          }
        } else {
          // Fallback to last-token heuristic
          const tailNorm = normalizeText(tailSeg);
          const tailTokens = tailNorm.split(/\s+/).filter(Boolean);
          const lastToken = tailTokens[tailTokens.length - 1] || "";

          for (let i = endItems.length - 1; i >= 0; i--) {
            if (endItems[i].text.toLowerCase().includes(lastToken)) {
              endIndex = i;
              logger.info("🎯 End token found via fallback", {
                lastToken,
                itemIndex: i,
                page: resolvedEndPage,
              });
              break;
            }
          }
        }

        // Build cross-page highlight plan
        const plan: Record<
          number,
          Array<{ left: number; top: number; width: number; height: number }>
        > = {};
        const fromPage = Math.min(startPage, resolvedEndPage);
        const toPage = Math.max(startPage, resolvedEndPage);
        let totalBoxes = 0;

        for (let p = fromPage; p <= toPage; p++) {
          const items = getPageTextItems(p);
          let fromIdx = 0;
          let toIdx = items.length - 1;

          if (p === startPage && p === resolvedEndPage) {
            fromIdx = Math.max(0, Math.min(startIndex, items.length - 1));
            toIdx = Math.max(fromIdx, Math.min(endIndex, items.length - 1));
          } else if (p === startPage) {
            fromIdx = Math.max(0, Math.min(startIndex, items.length - 1));
          } else if (p === resolvedEndPage) {
            toIdx = Math.max(0, Math.min(endIndex, items.length - 1));
          }

          for (let i = fromIdx; i <= toIdx; i++) {
            const it = items[i];
            const top = it.y - it.height * 0.85;

            plan[p] = plan[p] || [];
            plan[p].push({ left: it.x, top, width: it.width, height: it.height });
            totalBoxes++;
          }
        }

        setPageHighlights(plan);
        setMatchedPage(startPage);
        logger.info("🎨 Cross-page highlight planned", {
          fromPage,
          toPage,
          totalBoxes,
        });
      } else {
        logger.warn("⚠️ Could not determine start page; skipping highlight plan generation");
      }
    }
  }, [citationText, textLayer, onMatchResult]);

  // Render PDF pages
  React.useEffect(() => {
    if (!pdfDocument || !containerRef.current) return;

    let isMounted = true;

    const renderPages = async () => {
      const container = containerRef.current;

      if (!container) return;

      // Clear existing canvases
      container.innerHTML = "";
      canvasRefs.current.clear();
      highlightRefs.current.clear();

      const scale = 1.5; // Adjust for higher resolution

      for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
        if (!isMounted) return;

        try {
          const page = await pdfDocument.getPage(pageNum);
          const viewport = page.getViewport({ scale });

          // Create page container
          const pageDiv = document.createElement("div");

          pageDiv.className = "relative mb-4 shadow-medium";
          pageDiv.style.width = `${viewport.width}px`;
          pageDiv.style.height = `${viewport.height}px`;

          // Create canvas for PDF rendering
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          if (!context) continue;

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = "block";

          // Create highlight layer
          const highlightLayer = document.createElement("div");

          highlightLayer.className = "absolute top-0 left-0 pointer-events-none";
          highlightLayer.style.width = `${viewport.width}px`;
          highlightLayer.style.height = `${viewport.height}px`;

          pageDiv.appendChild(canvas);
          pageDiv.appendChild(highlightLayer);
          container.appendChild(pageDiv);

          canvasRefs.current.set(pageNum, canvas);
          highlightRefs.current.set(pageNum, highlightLayer);

          // Render PDF page
          await page.render({
            canvasContext: context,
            viewport: viewport,
            canvas: canvas,
          }).promise;

          logger.info("✅ Rendered page", { pageNum });
        } catch (err) {
          logger.error(`Failed to render page ${pageNum}`, err);
        }
      }
    };

    renderPages();

    return () => {
      isMounted = false;
    };
  }, [pdfDocument]);

  // Scroll to matched page and add highlight (including cross-page plan)
  React.useEffect(() => {
    if (matchedPage === null || !containerRef.current) return;

    // Wait for pages to be rendered before scrolling
    // Use a small delay to ensure DOM is ready
    const scrollTimeout = setTimeout(() => {
      const container = containerRef.current;

      if (!container) return;

      const pageElements = container.children;
      const pageElement = pageElements[matchedPage - 1] as HTMLElement;

      if (pageElement) {
        logger.info("📜 Scrolling to page", { matchedPage });
        pageElement.scrollIntoView({ behavior: "smooth", block: "center" });

        // If we have a cross-page plan, apply it to all pages involved
        const pages = Object.keys(pageHighlights).map((n) => parseInt(n, 10));

        if (pages.length > 0) {
          logger.info("🎨 Applying cross-page highlights", { pages: pages.join(", ") });
          for (const p of pages) {
            const layer = highlightRefs.current.get(p);

            if (!layer) continue;
            layer.innerHTML = "";
            const rects = pageHighlights[p] || [];

            for (const r of rects) {
              const hl = document.createElement("div");

              hl.className =
                "absolute bg-warning/30 dark:bg-warning/40 border border-warning rounded";
              hl.style.left = `${r.left}px`;
              hl.style.top = `${r.top}px`;
              hl.style.width = `${r.width}px`;
              hl.style.height = `${r.height}px`;
              hl.style.pointerEvents = "none";
              layer.appendChild(hl);
            }
            logger.info("🎨 Added highlights on page", { highlightCount: rects.length, page: p });
          }
        } else if (citationText) {
          // Fallback: single-page highlight (legacy behavior)
          const highlightLayer = highlightRefs.current.get(matchedPage);

          if (highlightLayer) {
            highlightLayer.innerHTML = "";
            const pageTextItems = textLayer.filter((item) => item.page === matchedPage);
            const pageText = pageTextItems.map((item) => item.text).join(" ");
            const matchResult = findTextMatch(pageText, citationText);

            if (matchResult) {
              const words = matchResult.matchedText.trim().split(/\s+/);
              const firstWord = words[0].toLowerCase();
              const lastWord = words[words.length - 1].toLowerCase();
              let firstIndex = -1;
              let lastIndex = -1;
              let foundFirst = false;

              pageTextItems.forEach((item, index) => {
                const itemTextLower = item.text.toLowerCase();

                if (!foundFirst && itemTextLower.includes(firstWord)) {
                  firstIndex = index;
                  foundFirst = true;
                }
                if (foundFirst && itemTextLower.includes(lastWord)) {
                  lastIndex = index;
                }
              });
              if (firstIndex !== -1 && lastIndex !== -1) {
                for (let i = firstIndex; i <= lastIndex; i++) {
                  const item = pageTextItems[i];
                  const highlight = document.createElement("div");
                  const top = item.y - item.height * 0.85;

                  highlight.className =
                    "absolute bg-yellow-300/40 border border-yellow-500 rounded";
                  highlight.style.left = `${item.x}px`;
                  highlight.style.top = `${top}px`;
                  highlight.style.width = `${item.width}px`;
                  highlight.style.height = `${item.height}px`;
                  highlight.style.pointerEvents = "none";
                  highlightLayer.appendChild(highlight);
                }
                logger.info("🎨 Added highlights on page (fallback)", {
                  highlightCount: lastIndex - firstIndex + 1,
                  page: matchedPage,
                });
              }
            }
          }
        }
      } else {
        logger.info("⚠️ Page element not found (may still be rendering)", { matchedPage });
      }
    }, 300); // 300ms delay to ensure pages are rendered

    return () => clearTimeout(scrollTimeout);
  }, [matchedPage, citationText, textLayer, pageHighlights]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner label="Loading PDF..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-danger-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[75vh] overflow-auto bg-default-100 p-4">
      <div ref={containerRef} className="flex flex-col items-center" />
      {numPages > 0 && (
        <div className="text-center text-sm text-default-500 mt-4">
          {numPages} {numPages === 1 ? "page" : "pages"}
        </div>
      )}
    </div>
  );
};

export default PDFViewer;
